/** Confirmation for permanent app-account deletion.
 *
 * Shared by Profile and the hard paywall: a newly created non-member account must be
 * deletable even though it cannot enter the member shell. Shopify is intentionally
 * separate, so the warning makes clear that deleting here does not cancel billing.
 */

import { RADIUS } from "@elyxeer/ui";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Drawer from "@mui/material/Drawer";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useAuthStore } from "~/store/auth-store";

interface DeleteAccountDrawerProps {
  open: boolean;
  onClose: () => void;
}

export function DeleteAccountDrawer({
  open,
  onClose,
}: DeleteAccountDrawerProps) {
  const { t } = useTranslation();
  const deleteAccount = useAuthStore((state) => state.deleteAccount);
  const [deleting, setDeleting] = useState(false);
  const [failed, setFailed] = useState(false);

  const confirm = async () => {
    setDeleting(true);
    setFailed(false);
    try {
      await deleteAccount();
    } catch {
      setFailed(true);
      setDeleting(false);
    }
  };

  const close = () => {
    setFailed(false);
    onClose();
  };

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={deleting ? undefined : close}
      aria-labelledby="delete-account-title"
      aria-describedby="delete-account-description"
      slotProps={{
        paper: {
          sx: {
            backgroundColor: "background.paper",
            backgroundImage: "none",
            borderTopLeftRadius: `${RADIUS.lg}px`,
            borderTopRightRadius: `${RADIUS.lg}px`,
            width: "100%",
            maxWidth: 520,
            marginInline: "auto",
          },
        },
      }}
    >
      <Box
        data-testid="account.delete-drawer"
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          padding: 3,
          paddingBottom: "calc(24px + env(safe-area-inset-bottom))",
        }}
      >
        <Box>
          <Typography
            id="delete-account-title"
            variant="H3"
            sx={{ color: "text.primary" }}
          >
            {t("profile.deleteAccount.title")}
          </Typography>
          <Typography
            id="delete-account-description"
            variant="T2"
            sx={{ color: "text.secondary", marginTop: 1 }}
          >
            {t("profile.deleteAccount.description")}
          </Typography>
        </Box>

        <Alert severity="warning">
          {t("profile.deleteAccount.subscriptionUnaffected")}
        </Alert>

        {failed ? (
          <Alert severity="error" data-testid="account.delete-error">
            {t("profile.deleteAccount.error")}
          </Alert>
        ) : null}

        <Button
          variant="contained"
          color="error"
          size="large"
          disabled={deleting}
          onClick={() => void confirm()}
          data-testid="account.delete-confirm"
        >
          {t("profile.deleteAccount.confirm")}
        </Button>
        <Button
          variant="outlined"
          color="inherit"
          size="large"
          disabled={deleting}
          onClick={close}
          data-testid="account.delete-cancel"
        >
          {t("profile.deleteAccount.cancel")}
        </Button>
      </Box>
    </Drawer>
  );
}
